import React, { Fragment, useEffect, useState } from "react";
import { Row, Col, Form, Input, Upload, message, Button } from "antd";
import { useProfileDetails, useUpdateProfileDetails } from "hooks/profile/profile";
import { PAGES, ACTIONS, usePermissions } from "hooks/permissions/permissions";
import Image from "helpers/Image";
import PrivateImage from "helpers/PrivateImage";
import { profile_image_type, strongPasswordRule } from "helpers/commonFunctions";
import "./style.css";

const Profile = () => {
  const [form] = Form.useForm();
  const [files, setFiles] = useState(null);
  const { data: profileData, refetch } = useProfileDetails();
  const { mutate: updateProfile, isLoading: loading } = useUpdateProfileDetails();
  const { can } = usePermissions();
  const canEdit = can(PAGES.PROFILE, ACTIONS.EDIT);

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: "images/*",
    onChange(info) {
      const file = info.fileList[0];
      const isJpgOrPng =
        file.type === "image/jpeg" || file.type === "image/png";
      if (!isJpgOrPng) {
        return false;
      }
      setFiles(info.fileList);
    },
    onDrop(e) {
      const file = e.dataTransfer.files[0];
      const isJpgOrPng =
        file.type === "image/jpeg" || file.type === "image/png";
      if (!isJpgOrPng) {
        return false;
      }
      setFiles(e.dataTransfer.files);
    },
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      form.setFieldsValue({ floorImage: null });
      setFiles(null);
    }
    return isJpgOrPng;
  };

  const validateMessages = {
    required: "Enter your email id!",
    types: {
      email: "Enter your email id!",
    },
  };

  const onFinish = (values) => {
    const formData = new FormData();
    if (files !== null) {
      formData.append("profileImage", files[0]?.originFileObj);
    }
    formData.append("firstName", values.firstName);
    formData.append("lastName", values.lastName);
    formData.append("email", values.email);
    if (values.password && values.password.length !== 0) {
      formData.append("password", values.password);
    }
    formData.append("roleId", profileData?.data?.roleId);
    updateProfile(formData, {
      onSuccess: (response) => {
        if (response.statusCode === 200) {
          message.success(response?.data);
          refetch();
        } else {
          message.error(response?.message);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });
  };

  useEffect(() => {
    form.setFieldsValue({
      firstName: profileData?.data?.firstName,
      lastName: profileData?.data?.lastName,
      email: profileData?.data?.email,
      role: profileData?.data?.roleName,
    });
  });

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      <Form
        className="form-page"
        form={form}
        onFinish={onFinish}
        colon={false}
        validateMessages={validateMessages}
        layout="vertical"
      >
        <div className="form-head form-head-sticky">
          <div className="form-head-left">
            {profileData !== undefined &&
              profileData?.data?.profileImageUrl !== null ? (
              <PrivateImage
                privateUrl={profileData?.data?.profileImageUrl}
                skeleton="form-head-avatar"
                className="form-head-avatar"
                imgType={profile_image_type}
              />
            ) : (
              <Image className="form-head-avatar" name="floorImage.png" />
            )}
            <div className="form-title">
              <h1>{`${profileData?.data?.firstName || "JA Security"} ${profileData?.data?.lastName || ""}`.trim()}</h1>
              <p>{profileData?.data?.email || "Manage your profile information"}</p>
            </div>
          </div>
          {!canEdit ? null : (
            <div className="form-actions">
              <Button className="form-submit" loading={loading} htmlType="submit">
                <Image className="save-img" name="save.svg" /> Update
              </Button>
            </div>
          )}
        </div>

        <div className="form-card">
          <section className="form-section">
            <div className="form-section-head">
              <h2>Account details</h2>
              <p>Your name, the email you sign in with and the role assigned to you.</p>
            </div>
            <Row gutter={[24, 0]}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: "Enter your first name" }]}>
                  <Input placeholder="First Name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label="Last Name" name="lastName">
                  <Input placeholder="Last Name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label="Email" name="email" rules={[{ required: true, type: "email" }]}>
                  <Input placeholder="Enter your email id" maxLength={40}></Input>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label="Role" name="role" rules={[{ required: false }]}>
                  <Input type="text" disabled={true} />
                </Form.Item>
              </Col>
            </Row>
          </section>

          <section className="form-section">
            <div className="form-section-head">
              <h2>Password</h2>
              <p>Leave both fields empty to keep your current password.</p>
            </div>
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label="New Password" name="password" rules={[{ required: false }, strongPasswordRule]}>
                  <Input.Password placeholder="Enter password" autoComplete="new-password" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Confirm Password"
                  name="confirmPassword"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }

                        return Promise.reject(
                          new Error(
                            "new passwords and confirm password are not the same!"
                          )
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="Confirm password" autoComplete="new-password" />
                </Form.Item>
              </Col>
            </Row>
          </section>

          <section className="form-section">
            <div className="form-section-head">
              <h2>Photo</h2>
              <p>Shown next to your name across the app.</p>
            </div>
            <Form.Item label="" name="profileImage" rules={[{ required: false }]}>
              <div className="form-upload">
                <div className="form-upload-preview">
                  {files === null ? (
                    profileData !== undefined &&
                      profileData?.data?.profileImageUrl !== null ? (
                      <PrivateImage
                        privateUrl={profileData?.data?.profileImageUrl}
                        className="profile-img"
                        imgType={profile_image_type}
                      />
                    ) : (
                      <Image className="profile-img" name="floorImage.png" />
                    )
                  ) : (
                    <img
                      src={URL.createObjectURL(files[0]?.originFileObj)}
                      alt=".."
                      className="profile-img"
                    />
                  )}
                </div>
                <Upload
                  {...uploadProps}
                  showUploadList={false}
                  maxCount={1}
                  beforeUpload={beforeUpload}
                >
                  <div className="form-upload-drop">
                    <Image name="Gallery.svg" />
                    Click to upload
                    <p>PNG, JPG or GIF (max 400x400px)</p>
                  </div>
                </Upload>
              </div>
            </Form.Item>
          </section>
        </div>
      </Form>
    </Fragment>
  );
};

export default Profile;
